import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  Platform,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore, Country } from '../../store/appStore';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface CountryPickerProps {
  /** Country name (matches Country.name in the store's country list) */
  value: string;
  onChange: (name: string) => void;
  label?: string;
  error?: string;
}

const CountryRow: React.FC<{
  item: Country;
  selected: boolean;
  onSelect: (name: string) => void;
}> = ({ item, selected, onSelect }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <TouchableOpacity
    style={[styles.item, selected && styles.itemSelected]}
    onPress={() => onSelect(item.name)}
    activeOpacity={0.7}
  >
    <Text style={styles.itemFlag}>{item.flag}</Text>
    <Text style={[styles.itemName, selected && styles.itemNameSelected]} numberOfLines={1}>{item.name}</Text>
    {selected && <Ionicons name="checkmark" size={18} color={colors.primary} />}
  </TouchableOpacity>
);
};

export const CountryPicker: React.FC<CountryPickerProps> = ({
  value,
  onChange,
  label = 'Country',
  error,
}) => {
  const styles = useThemedStyles(getStyles);
  const countries = useAppStore((state) => state.countries);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => countries.find((c) => c.name === value), [countries, value]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => c.name.toLowerCase().includes(q));
  }, [countries, search]);

  const handleSelect = (name: string) => {
    onChange(name);
    setOpen(false);
    setSearch('');
  };

  if (Platform.OS === 'web') {
    return (
      <View style={styles.wrapper}>
        {label ? <Text style={styles.label}>{label}</Text> : null}
        <TouchableOpacity
          style={[styles.trigger, error ? styles.triggerError : undefined, open && styles.triggerOpen]}
          onPress={() => setOpen((v) => !v)}
          activeOpacity={0.7}
        >
          <View style={styles.triggerLeft}>
            {selected ? <Text style={styles.flag}>{selected.flag}</Text> : null}
            <Text style={styles.name} numberOfLines={1}>{value || 'Select country'}</Text>
          </View>
          <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textLight} />
        </TouchableOpacity>
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        {open && (
          <View style={styles.webDropdown}>
            <View style={styles.webSearchRow}>
              <Ionicons name="search-outline" size={15} color={colors.textLight} />
              <TextInput
                style={styles.webSearchInput}
                placeholder="Search country..."
                placeholderTextColor={colors.textLight}
                value={search}
                onChangeText={setSearch}
                autoCorrect={false}
                autoCapitalize="none"
                autoFocus
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Ionicons name="close-circle" size={15} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>
            <ScrollView style={styles.webList} nestedScrollEnabled showsVerticalScrollIndicator={false}>
              {filtered.map((item) => (
                <CountryRow key={item.code} item={item} selected={item.name === value} onSelect={handleSelect} />
              ))}
              {filtered.length === 0 && (
                <View style={styles.emptyRow}>
                  <Text style={styles.emptyText}>No countries found</Text>
                </View>
              )}
            </ScrollView>
          </View>
        )}
      </View>
    );
  }

  return (
    <>
      <View style={styles.wrapper}>
        {label ? <Text style={styles.label}>{label}</Text> : null}
        <TouchableOpacity
          style={[styles.trigger, error ? styles.triggerError : undefined]}
          onPress={() => setOpen(true)}
          activeOpacity={0.7}
        >
          <View style={styles.triggerLeft}>
            {selected ? <Text style={styles.flag}>{selected.flag}</Text> : null}
            <Text style={styles.name} numberOfLines={1}>{value || 'Select country'}</Text>
          </View>
          <Ionicons name="chevron-down" size={16} color={colors.textLight} />
        </TouchableOpacity>
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
      </View>

      <Modal visible={open} animationType="slide" transparent>
        <View style={styles.overlay}>
          <View style={styles.sheet}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Select Country</Text>
              <TouchableOpacity
                onPress={() => { setOpen(false); setSearch(''); }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close" size={22} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <View style={styles.searchRow}>
              <Ionicons name="search-outline" size={16} color={colors.textLight} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search country..."
                placeholderTextColor={colors.textLight}
                value={search}
                onChangeText={setSearch}
                autoCorrect={false}
                autoCapitalize="none"
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Ionicons name="close-circle" size={16} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>
            <FlatList
              data={filtered}
              keyExtractor={(item) => item.code}
              renderItem={({ item }) => (
                <CountryRow item={item} selected={item.name === value} onSelect={handleSelect} />
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            />
          </View>
        </View>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: { gap: 4 },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: 4,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    gap: spacing.sm,
  },
  triggerOpen: {
    borderColor: colors.primary,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 0,
  },
  triggerError: { borderColor: colors.error },
  triggerLeft: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  flag: { fontSize: typography.fontSize.base },
  name: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    flex: 1,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },
  webDropdown: {
    borderWidth: 1.5,
    borderTopWidth: 0,
    borderColor: colors.primary,
    borderBottomLeftRadius: designTokens.borderRadius.md,
    borderBottomRightRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
    ...designTokens.shadows.sm,
  },
  webSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.sm,
    backgroundColor: colors.lightGray,
  },
  webSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    outlineStyle: 'none',
  } as any,
  webList: { maxHeight: 320 },
  emptyRow: { padding: spacing.lg, alignItems: 'center' },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    ...designTokens.shadows.lg,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sheetTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? spacing.sm : 0,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.lightGray,
    gap: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    paddingVertical: Platform.OS === 'android' ? spacing.sm : 0,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    gap: spacing.md,
  },
  itemSelected: { backgroundColor: (colors.primary as unknown as string) + '10' },
  itemFlag: { fontSize: typography.fontSize.lg, width: 28, textAlign: 'center' },
  itemName: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
  },
  itemNameSelected: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  separator: { height: 1, backgroundColor: (colors.border as unknown as string) + '60' },
});
